import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { SavingStatus, Waypoint, WaypointData, WaypointRaw } from "types/app";
import { getUrlsIfAreValidImages } from "utils/api/checkIfUrlIsImage";
import { postToApi } from "utils/api/postToApi";
import {
  parseWaypointForAPI,
  parseWaypointFromAPI,
} from "utils/parsers/waypointParser";
import { fitCoordsToScreen, fitWaypointToScreen } from "utils/map/fitToScreen";
import { showWaypointDeletionSuccessNotification } from "utils/notifications/waypointNotifications";
import { useAuth } from "./useAuth";
import { useEditMode } from "./useEditMode";
import { useGenericModals } from "../hooks/useGenericModals";
import { useHiddenIds } from "../hooks/useHiddenIds";
import { useMapContext } from "./useMapContext";
import { useUserTracks } from "./useUserTracks";
import { useImageUpload } from "../hooks/useImageUpload";
import { showErrorNotification } from "utils/notifications/customNotifications";

type SelectWaypointOptions = {
  focus?: boolean;
};

type CreateWaypointOptions = {
  focus?: boolean;
};

/** The changed data of a waypoint, the rest stays as it is */
export type UpdateWaypointData = Partial<WaypointData>;
/** A new waypoint needs only its place, the rest has defaults */
export type CreateWaypointData = UpdateWaypointData &
  Pick<WaypointData, "point">;

const NEW_WAYPOINT_DATA: Omit<WaypointData, "point"> = {
  name: "Waypoint",
  desc: "",
  links: [],
  icon: "",
  cmt: "",
  src: "Trackbook",
  type: "",
  visible: true,
};

interface UserWaypoints {
  waypoints: Waypoint[] | null;
  hiddenWaypointIds: number[];
  fetchWaypoints: () => Promise<WaypointRaw[] | undefined>;
  selectedWaypoint: Waypoint | null;
  selectWaypoint: (
    id: number | null,
    options?: SelectWaypointOptions
  ) => Waypoint | null;
  /** Throws when the waypoint couldn't be saved, the caller tells the user (e.g. `showWaypointUpdateFailedNotification`) */
  updateWaypoint: (
    id: Waypoint["id"],
    updatedData: UpdateWaypointData
  ) => Promise<unknown>;
  deleteWaypoints: (id: number | number[]) => Promise<unknown>;
  /** Throws when the waypoint couldn't be saved, the caller tells the user (e.g. `showWaypointCreationFailedNotification`) */
  createWaypoint: (
    data: CreateWaypointData,
    options?: CreateWaypointOptions
  ) => Promise<unknown>;
  toggleWaypointVisibility: (waypointId: number) => void;
  isAddingWaypoint: boolean;
  setIsAddingWaypoint: (value: React.SetStateAction<boolean>) => void;
  isFetching: boolean;
  waypointStatus: SavingStatus;
  getImagesFromLinks: () => Promise<string[]>;
  getSelectedWaypointImages: () => Promise<string[]>;
}

const UserWaypointsContext = React.createContext<UserWaypoints>({
  waypoints: null,
  hiddenWaypointIds: [],
  fetchWaypoints: () => Promise.resolve(undefined),
  selectedWaypoint: null,
  selectWaypoint: () => null,
  updateWaypoint: () => Promise.resolve(null),
  deleteWaypoints: () => Promise.resolve(null),
  createWaypoint: () => Promise.resolve(null),
  isAddingWaypoint: false,
  setIsAddingWaypoint: () => null,
  isFetching: false,
  waypointStatus: "unsaved",
  getImagesFromLinks: () => Promise.resolve([]),
  getSelectedWaypointImages: () => Promise.resolve([]),
  toggleWaypointVisibility: () => undefined,
});

type GetWaypointsResponse = {
  waypoints: WaypointRaw[];
};

/**
 * The waypoint as it's shown until the saved one is loaded from the server. A new one gets a temporary id that
 * no saved waypoint has (negative) and placeholders of what the server sets.
 */
const getShownWaypoint = (
  data: WaypointData,
  saved: Waypoint | undefined
): Waypoint => ({
  id: -Date.now(),
  userId: -1,
  ele: 0,
  localTime: "",
  createdAt: new Date(),
  updatedAt: new Date(),
  ...saved,
  ...data,
});

/** The data of a waypoint that's saved, without what the server sets */
const getWaypointData = ({
  name,
  desc,
  links,
  point,
  icon,
  cmt,
  src,
  type,
  visible,
}: Waypoint): WaypointData => ({
  name,
  desc,
  links,
  point,
  icon,
  cmt,
  src,
  type,
  visible,
});

export const useProvideUserWaypoints = (): UserWaypoints => {
  const { map } = useMapContext();
  const [waypoints, setWaypoints] = useState<Waypoint[] | null>(null);
  const [selectedWaypointId, setSelectedWaypointId] = useState<number | null>(
    null
  );
  const [isAddingWaypoint, setIsAddingWaypoint] = useState(false);
  const [waypointStatus, setWaypointStatus] = useState<SavingStatus>("unsaved");
  const [isFetching, setIsFetching] = useState(false);
  const {
    hiddenIds: hiddenWaypointIds,
    setHiddenFrom,
    setHidden,
    toggle,
  } = useHiddenIds();
  const { selectedTrack, leaveOpenTrack } = useUserTracks();
  const { setEditMode, editMode } = useEditMode();
  const { openConfirmLosingTrackModal, openErrorModal } = useGenericModals();
  const { t } = useTranslation();
  const {
    state: { language },
  } = useOptions();
  const { updateStorageSize } = useImageUpload();
  const { user } = useAuth();
  const waypointsRef = useRef(waypoints);
  const editModeRef = useRef(editMode);
  const isTrackSavedRef = useRef(!!selectedTrack);

  useEffect(() => {
    waypointsRef.current = waypoints;
  }, [waypoints]);

  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  useEffect(() => {
    isTrackSavedRef.current = !!selectedTrack;
  }, [selectedTrack]);

  useEffect(() => {
    if (!isAddingWaypoint) return;
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsAddingWaypoint(false);
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isAddingWaypoint]);

  useEffect(() => {
    if (user) return;
    setWaypointStatus("unsaved");
    setWaypoints(null);
    setHiddenFrom(null);
    setSelectedWaypointId(null);
    setIsAddingWaypoint(false);
    setIsFetching(false);
  }, [setHiddenFrom, user]);

  // The waypoints are fetched when the user signs in, not again when the user's settings switch the language
  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const fetchWaypoints = useCallback(async () => {
    if (!user) return Promise.reject(new Error("not_signed_in"));
    try {
      const data = await postToApi<GetWaypointsResponse>(
        "get-waypoints",
        languageRef.current
      );
      const parsedWaypoints = data.waypoints.map(parseWaypointFromAPI);

      setWaypoints(parsedWaypoints);
      setHiddenFrom(parsedWaypoints);
      return data.waypoints;
    } catch (_error) {
      setWaypoints(null);
      setHiddenFrom(null);
      console.error("error getting waypoints");
      return;
    }
  }, [setHiddenFrom, user]);

  /**
   * Saves the waypoint (`id` -1 for a new one). It's shown changed right away, then the saved waypoints are loaded
   * from the server - also when saving fails, which restores the waypoint as it was.
   */
  const saveWaypoint = useCallback(
    async (id: number, data: WaypointData) => {
      if (!user) throw new Error("not_signed_in");

      setWaypointStatus("saving");
      const shownWaypoint = getShownWaypoint(
        data,
        waypointsRef.current?.find((waypoint) => waypoint.id === id)
      );
      setWaypoints((waypoints) =>
        id === -1
          ? [...(waypoints ?? []), shownWaypoint]
          : (waypoints ?? []).map((waypoint) =>
              waypoint.id === id ? shownWaypoint : waypoint
            )
      );
      setHidden([shownWaypoint.id], !data.visible);

      try {
        await postToApi("update-waypoint", language, {
          ...parseWaypointForAPI({ id, ...data }),
        });
        await fetchWaypoints();
        setWaypointStatus("saved");
      } catch (error) {
        setWaypointStatus("unsaved");
        await fetchWaypoints();
        // Callers tell the user, each in its own way (or roll back their own optimistic updates)
        throw error;
      }
    },
    [fetchWaypoints, language, setHidden, user]
  );

  const updateWaypoint = useCallback(
    (id: number, changes: UpdateWaypointData) => {
      const waypoint = waypointsRef.current?.find(
        (waypoint) => waypoint.id === id
      );
      if (!waypoint) return Promise.reject(new Error("Waypoint not found"));
      return saveWaypoint(id, { ...getWaypointData(waypoint), ...changes });
    },
    [saveWaypoint]
  );

  const createWaypoint = useCallback(
    async (data: CreateWaypointData, options?: CreateWaypointOptions) => {
      await saveWaypoint(-1, { ...NEW_WAYPOINT_DATA, ...data });
      if (options?.focus === true && map)
        fitCoordsToScreen(data.point, map, 14);
    },
    [map, saveWaypoint]
  );

  const deleteWaypoints = useCallback(
    async (id: number | number[]) => {
      const ids = Array.isArray(id) ? id : [id];
      const deletedName =
        ids.length === 1
          ? waypointsRef.current?.find((waypoint) => waypoint.id === ids[0])
              ?.name
          : undefined;

      // Removed right away; when deleting fails, they're loaded from the server again
      setWaypoints(
        (waypoints) =>
          waypoints?.filter((waypoint) => !ids.includes(waypoint.id)) ?? []
      );
      setHidden(ids, false);

      try {
        await postToApi("delete-waypoint", language, {
          id: Array.isArray(id) ? undefined : id,
          ids: Array.isArray(id) ? id : undefined,
        });
      } catch (_error) {
        await fetchWaypoints();
        openErrorModal({
          title: t("generic.error_title"),
          text: t("errors.error_deleting_waypoint.text"),
        });
        return;
      }

      if (deletedName) {
        showWaypointDeletionSuccessNotification({ name: deletedName });
      } else if (ids.length > 1) {
        showWaypointDeletionSuccessNotification({ count: ids.length });
      }
      // Their images were deleted with them
      await updateStorageSize();
      await fetchWaypoints();
    },
    [fetchWaypoints, language, openErrorModal, setHidden, t, updateStorageSize]
  );

  const toggleWaypointVisibility = useCallback(
    (waypointId: number) => {
      toggle(waypointId, (id, visible) =>
        updateWaypoint(id, { visible })
      ).catch(() =>
        showErrorNotification({
          title: t("generic.error_title"),
          message: t(
            "track_and_waypoint_visibility.waypoint_visibility_toggle_failure_text"
          ),
        })
      );
    },
    [t, toggle, updateWaypoint]
  );

  const selectWaypoint = useCallback(
    (id: number | null, options?: SelectWaypointOptions) => {
      if (id === null) {
        setSelectedWaypointId(null);
        return null;
      }
      if (!waypointsRef.current) throw new Error("Waypoints empty");
      const waypoint = waypointsRef.current?.find((point) => point.id === id);
      if (!waypoint) throw new Error("Waypoint not found");

      const select = () => {
        setWaypointStatus("saved");
        setSelectedWaypointId(id);
        setEditMode("waypoint");
        if (map && options?.focus) fitWaypointToScreen(waypoint.point, map);
      };

      if (editModeRef.current === "track" && !isTrackSavedRef.current) {
        openConfirmLosingTrackModal({ onConfirm: select });
        return null;
      }
      // An own track asks when its latest change isn't saved yet
      return leaveOpenTrack(select) ? waypoint : null;
    },
    [leaveOpenTrack, map, openConfirmLosingTrackModal, setEditMode]
  );

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      setIsFetching(true);
      await fetchWaypoints();
      setIsFetching(false);
    };
    void fetch();
  }, [fetchWaypoints, user]);

  const selectedWaypoint = useMemo(() => {
    const foundWaypoint = waypoints?.find(
      (waypoint) => waypoint.id === selectedWaypointId
    );
    // return a new copy to prevent accident original array mutation
    return foundWaypoint ? { ...foundWaypoint } : null;
  }, [selectedWaypointId, waypoints]);

  /**
   * Returns a list of images that are present in links in all waypoints
   */
  const getImagesFromLinks = async () => {
    const allLinks = waypoints?.reduce<string[]>(
      (links, waypoint) => [...links, ...waypoint.links],
      []
    );
    if (!allLinks || allLinks.length === 0) return [];
    const imageUrls = await getUrlsIfAreValidImages(allLinks);
    return imageUrls;
  };

  const getSelectedWaypointImages = useCallback(async () => {
    if (!selectedWaypoint || selectedWaypoint.links.length < 1) return [];
    const images = await getUrlsIfAreValidImages(selectedWaypoint.links);
    return images;
  }, [selectedWaypoint]);

  return {
    waypoints,
    hiddenWaypointIds,
    fetchWaypoints,
    selectedWaypoint,
    selectWaypoint,
    updateWaypoint,
    deleteWaypoints,
    createWaypoint,
    toggleWaypointVisibility,
    isAddingWaypoint,
    setIsAddingWaypoint,
    isFetching,
    waypointStatus,
    getImagesFromLinks,
    getSelectedWaypointImages,
  };
};

export const useUserWaypoints = () => useContext(UserWaypointsContext);

export const UserWaypointDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const userWaypointData = useProvideUserWaypoints();
  return (
    <UserWaypointsContext.Provider value={userWaypointData}>
      {children}
    </UserWaypointsContext.Provider>
  );
};
